import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { GetCartContents,GetAddOns,GetPasses,convertTo12HourFormat,syncCartToServer, GetServiceFees, GetCharity, getAllInPrice } from '../api-funcs'; 
import { useCart,usePromoCodeInput,EventContext } from '../CartContext';

export default function CartPage() {
    const { globalEventId } = useContext(EventContext);
    const { promoCodeInput, updatePromoCode } = usePromoCodeInput();
    const { globalCartId } = useCart();
    const { SetGlobalCartId } = useCart();
    const { settings } = useContext(EventContext);

    const [cartInfo, setCartInfo] = useState([]);
    const [editingItem, setEditingItem] = useState(null); // Track the item being edited
    const [quantity, setQuantity] = useState(0); // Track the updated quantity
    const [cartMsg, setCartMsg] = useState('');
    const [ptcActive, setPtcActive] = useState(true);
    const navigate = useNavigate();
    const [cartFees, setFees] = useState([]);
    const [taxRate, setTaxRate] = useState(0);
    const [roundupCents, setRoundupCents] = useState(0);  
    const [grandTotalCents, setGrandTotalCents] = useState(0);
    const [roundupResetToken, setRoundupResetToken] = useState(0);
    const [charityInfo, setCharityInfo] = useState(null);

    const [searchParams] = useSearchParams();
    const urlPromoCode = searchParams.get('promocode'); // Get 'promocode' from URL

    let subTotal = 0;
    let grandTotal = 0;
    let discountTotal = 0;
    let ticketQty = 0;
    let serviceFees = 0;

    //read the cart id from the url
    const {id} = useParams();

    //if a cart id was passed in the url then use it
    if (id && !globalCartId.cartID) {
      SetGlobalCartId({
        cartID: id
      });
      localStorage.setItem('cartID', id);
    }

    const [loadingItemId, setLoadingItemId] = useState(null);
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
      
    useEffect(() => {
      if (urlPromoCode) {
        updatePromoCode(globalEventId,urlPromoCode);
      }
    }, []);

    useEffect(() => {
      async function fetchFees() {
        try {
          const res = await GetServiceFees(globalEventId);
          //const data = await res.json();
          setFees(res);
          setTaxRate(res[0]?.EF_TAX_RATE || 0);
        } catch (error) {
          console.error('Error fetching fees:', error);
        } 
      }

      fetchFees();
    }, []);

    useEffect(() => {
      if (!settings?.ES_CHARITY_ID) return;
      
      async function fetchCharity() {
        if (settings?.ES_CHARITY_ID) {
          const charity = await GetCharity(globalEventId);
          setCharityInfo(charity);
        }
      }
      fetchCharity();
    }, [globalEventId, settings]);

    async function fetchCartContents(cartIdOverride) {
      const cartId = cartIdOverride !== undefined ? cartIdOverride : globalCartId.cartID;
      const cartData = await GetCartContents(cartId) || [];

      const passData = await GetPasses(globalEventId);
      for (const pass of passData) {
          if (!cartData.some(item=>String(item.definition_id) === String(pass.definition_id))) {

              const cartItem = {
                  EVENT_DATE:null,
                  NAME: pass.name,
                  base_price: parseFloat(pass.base_price),
                  cart_id: cartId,
                  cart_qty: 0,
                  definition_id: pass.definition_id,
                  promo_price: pass.promo_price,
                  ticket_id: pass.ticket_id,
                  ticket_type: pass.ticket_type
              }
              cartData.push(cartItem);
          }
      }

      const addOnData = await GetAddOns(globalEventId);

      for (const addOn of addOnData) {
          if (!cartData.some(item=>item.definition_id === addOn.definition_id)) {

              const cartItem = {
                  EVENT_DATE:null,
                  NAME: addOn.name,
                  DESCRIPTION: addOn.description,
                  IMAGE_NAME: addOn.image_name,
                  base_price: parseFloat(addOn.base_price),
                  cart_id: cartId,
                  cart_qty: 0,
                  definition_id: addOn.definition_id,
                  promo_price: addOn.promo_price,
                  ticket_id: addOn.ticket_id,
                  ticket_type: addOn.ticket_type
              }
              cartData.push(cartItem);
          }
      }


     setCartInfo(cartData);
     setPtcActive(!cartData.some(item => item.cart_qty > 0));
    }

    //initial load of cart data on mount
    useEffect(() => {

      fetchCartContents();

    }, [globalCartId.cartID]);
  
    // Handle editing an item (open modal and set initial quantity)
    const handleEditClick = (item) => {
      if (item.ticket_type != 4) {
        setEditingItem(item);
        setQuantity(item.cart_qty); // Set initial quantity for editing
      }
    };
  
    //calculate page totals
    const totals = React.useMemo(() => {
      if (cartInfo.length === 0 || cartFees.length === 0) {
        return { subTotal:0, discountTotal:0, ticketQty:0, serviceFees:0, taxTotal:0, grandTotal:0 };
      }

      const results = calcTotals(cartInfo, cartFees, taxRate);
      
      setGrandTotalCents(results.grandTotal * 100);
      return results

    }, [cartInfo, cartFees, taxRate]);    


    // Handle saving the updated quantity for the item
    const handleSave = () => {

      if (editingItem) {
        // If reducing to 0, check if any add-on depends on this ticket's category
        if (quantity === 0 && editingItem.ticket_category) {
          const dependents = cartInfo.filter(
            (item) =>
              item.requires_ticket_category === editingItem.ticket_category &&
              item.cart_qty > 0
          );
          if (dependents.length > 0) {
            const names = dependents.map((d) => d.NAME).join(', ');
            setCartMsg(`You must remove "${names}" before removing this ticket.`);
            setEditingItem(null);
            return;
          }
        }

        clearRoundup();
        const updatedItem = { ...editingItem, cart_qty: quantity };
        handleUpdateItem(updatedItem);
      }
    };
  
    // Update the cart state with the modified item
    const handleUpdateItem = async (updatedItem) => {
      
        let merchTimeslotId = null;
        setLoadingItemId(updatedItem.ticket_id)      // show spinner

        // Calculate the updated results
        const updatedResults = cartInfo
        .sort((a, b) => a.ticket_type - b.ticket_type)
        .map((item) => {
            //store standard ticket timeslot id so it can be used to set the timeslot id for merchandise
            if (item.ticket_type === 1)
              merchTimeslotId = item.ticket_id;
            
            if (item.rowId === updatedItem.rowId) {
                if (updatedItem.cart_qty === 0)
                    updatedItem.toDelete = true;

                // If the item is a merchandise item, set the timeslot ID
                //unless this is a 'merch only' cart, then keep it's current ticket_id
                if ((item.ticket_type === 3) && (item.ticket_id != merchTimeslotId) && merchTimeslotId !=null) {
                    updatedItem.ticket_id = merchTimeslotId;
                }

                return updatedItem;
            }
            else {
                return item;
            }
          });
      
        // Clear the editing state
        setEditingItem(null);
        
        const allowedKeys = ["cart_qty", 
                              "ticket_type", 
                              "ticket_id", 
                              "definition_id", 
                              "cart_item_id", 
                              "replacement", 
                              "toDelete",
                              "ticket_category"
                            ];

        // Map over newCartContents and extract only allowed keys
        const filteredNewCartContents = updatedResults.map(item =>
          Object.fromEntries(Object.entries(item).filter(([key]) => allowedKeys.includes(key)))
        );

        let newCartId = null;

        try {

            newCartId = await syncCartToServer({
              eventId:globalEventId,
              cartID: globalCartId.cartID,
              promoCode: promoCodeInput,
              cartContents: filteredNewCartContents
            });
          
            // Check for null result from syncCartToServer
            if (!newCartId) {
              setCartMsg('Available ticket quantity exceeded');
            }
            else if (newCartId === -1) {
              setCartMsg('Available ticket quantity exceeded');
            }
            else {
              // Persist the cart ID so it survives a page refresh
              localStorage.setItem('cartID', newCartId.cartId);
              // Update state with the synced cart ID and contents
              SetGlobalCartId({
                cartID: newCartId.cartId,
                cartContents: newCartId.cartContents,
              });

              updatePromoCode(globalEventId,newCartId.promoCode);

              await fetchCartContents(newCartId.cartId);
              setCartMsg('');
            }
        } catch (error) {
          console.error('Error syncing cart to server:', error);      
        }    
      setLoadingItemId(null)
    };

    const clearRoundup = () => {

      if (settings?.ES_CHARITY_ID && roundupCents > 0) {
        // zero out UI totals related to roundup
        setRoundupCents(0);
        setGrandTotalCents(Math.round(totals?.grandTotal * 100));
        // persist "off" to server
        saveCartRoundUp(0);
        // signal the checkbox to uncheck
        setRoundupResetToken(t => t + 1);
      }
    };

    async function saveCartRoundUp(charityAmt) {

      //this is necesssary because we need to send the cart contents to synccarttoserver for it to process the roundup setting
      const allowedKeys = ["cart_qty", 
                            "ticket_type", 
                            "ticket_id", 
                            "definition_id", 
                            "cart_item_id", 
                            "replacement", 
                            "toDelete",
                            "ticket_category"
                          ];

        // Map over newCartContents and extract only allowed keys
        const filteredNewCartContents = cartInfo.map(item =>
          Object.fromEntries(Object.entries(item).filter(([key]) => allowedKeys.includes(key)))
        );

        await syncCartToServer({
          eventId:globalEventId,
          cartID: globalCartId.cartID,
          promoCode: promoCodeInput,
          cartContents: filteredNewCartContents,
          charityId:settings?.ES_CHARITY_ID,
          charityAmt:charityAmt
      });
    }

    let dateBlock = '';
    let header = '';
    let ticketDate = '';
    let ticketTime = '';
    
    return (
      <Container className="mt-2">
        <Row>
          <Col className='text-start'>
            <span
              onClick={() => navigate('/')}
              style={{ 
                textDecoration: 'none', 
                fontSize: '12px', 
                color: 'blue', 
                cursor: 'pointer' 
              }}
            >
            {'<< Back to tickets'}
          </span>          
        </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <h3>Shopping Cart</h3>
            <p>{cartMsg}</p>
          </Col>
        </Row>

        <Row>
          <Col>
            <CartPromoCode 
              cartInfo={cartInfo} 
              refreshCart={fetchCartContents} 
              onApplyingPromo={setIsApplyingPromo}
              onClearRoundup={clearRoundup}
            />
          </Col>
        </Row>  


        <Modal show={editingItem !== null} onHide={() => setEditingItem(null)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingItem?.NAME} - Edit Quantity</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingItem?.IMAGE_NAME && (
              <div className='text-center'>
                <img className="modal-cart-image" src={`${process.env.REACT_APP_CDN_URL}/${settings?.EVENT_URL}/${editingItem.IMAGE_NAME}`} />
              </div>
            )}
            <div className="d-flex align-items-center justify-content-center">
              <Button 
                variant="secondary" 
                onClick={() => setQuantity(prev => Math.max(0, prev - 1))}
              >
                -
              </Button>
              <Form.Control
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                min="0"
                style={{ width: '60px', textAlign: 'center', margin: '0 10px' }}
              />
              <Button 
                variant="secondary" 
                onClick={() => setQuantity(prev => prev + 1)}
              >
                +
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setEditingItem(null)}>
              Close
            </Button>
            <Button variant="primary" className="btn-atc" onClick={handleSave}>
              Save Changes
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Cart Sections */}
        {/* Tickets Section */}
        {cartInfo.filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5).length > 0 && (
          <Row className="mb-2">
            <Col xs={12} className="mb-3">
              <Card>
                <Card.Body className='px-2'>
                  <Card.Title className="text-start">Tickets</Card.Title>
                  {cartInfo
                    .filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5)
                    .map((item,index) => {
                      item.rowId = '1-'+index;
                      let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                      let lineTotal = itemPrice * item.cart_qty;
                      
                      //don't charge service fees on replacement tickets
                      if ((item.ticket_type === 1 || item.ticket_type === 5) && item?.replacement == null) {
                        ticketQty += item.cart_qty
                        
                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 1 || fee.EF_TICKET_TYPE === 5)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1 || fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1 || fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }
                      }

                      subTotal = subTotal + lineTotal;

                      if (item?.promo_price > 0)
                        discountTotal = discountTotal + ((item.base_price - item.promo_price) * item.cart_qty);

                      //display all-in price (no fee on replacement tickets, same as calcTotals)
                      const displayPrice = item?.replacement != null
                        ? Number(itemPrice)
                        : getAllInPrice(itemPrice, item.ticket_type, cartFees);
                      itemPrice = displayPrice.toFixed(2);
                      lineTotal = (displayPrice * item.cart_qty).toFixed(2);

                      //if the subtotal for regular tickets is $0 then don't charge any service fees on them.
                      if (subTotal == 0)
                        serviceFees = 0;

                    if (item.ticket_type === 1 || item.ticket_type === 5) {
                      if (dateBlock != item.EVENT_DATE) {
                          dateBlock = item.EVENT_DATE;
                          ticketDate = item.EVENT_DATE.substring(0, 10); 
                          ticketTime = convertTo12HourFormat(item.EVENT_DATE.substring(11, 16)); 
                          header = ticketHeader(ticketDate,ticketTime);
                      }
                      else
                          header = '';
                    }
                    else
                      header = '';

                      return (
                        <>
                        {header}
                        <Row key={1-index}>
                          <Col xs={4} className="text-start cartColDetail">{item.NAME}</Col>
                          <Col xs={1} className="cartColDetail">{item.cart_qty}</Col>
                          <Col xs={3} className="text-end cartColDetail">${itemPrice}</Col>
                          <Col xs={3} className="text-end cartColDetail">${lineTotal}</Col>
                          <Col xs={1} className='px-0' >
                                {(item.ticket_type === 1 || item.ticket_type === 5) && item?.replacement === null && (
                                  loadingItemId === item.ticket_id || isApplyingPromo ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                    </>
                                  ) : (
                                    <i
                                      className="bi bi-pencil-square"
                                      onClick={() => !isApplyingPromo && handleEditClick(item)}
                                      style={{ cursor: isApplyingPromo ? 'not-allowed' : 'pointer', opacity: isApplyingPromo ? 0.5 : 1 }}
                                    />
                                  )
                                )}
                          </Col>
                        </Row>
                        </>
                      );
                    })}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
    
        {/* Passes Section */}
        {cartInfo.filter((item) => item.ticket_type === 2 && item.cart_qty > 0).length > 0 && (
          <Row className="mb-2">
          <Col xs={12} className="mb-3">
            <Card>
              <Card.Body className='px-2'>
                <Card.Title className="text-start">Passes</Card.Title>
                <Row>
                <Col xs={4} className="text-start fw-bold cartColHeading pl-5">Item</Col>
                  <Col xs={1} className="text-center fw-bold cartColHeading px-0">Qty</Col>
                  <Col xs={3} className="text-end fw-bold cartColHeading">Price</Col>
                  <Col xs={3} className="text-end fw-bold cartColHeading">Total</Col>
                  <Col xs={1}></Col>
                </Row>

                {cartInfo
                  .filter((item) => item.ticket_type === 2)
                  .map((item,index) => {
                    
                    item.rowId = '2-'+index;
                    let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                    let lineTotal = itemPrice * item.cart_qty;
                    subTotal = subTotal + lineTotal;

                    if (item?.promo_price > 0)
                    discountTotal = discountTotal + ((item.base_price - item.promo_price) * item.cart_qty);

                      if (item.ticket_type === 2 && item?.replacement === null) {
                        ticketQty += item.cart_qty
                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }
                      }


                    itemPrice = itemPrice.toFixed(2);
                    lineTotal = lineTotal.toFixed(2);

                    //if the subtotal is $0 then don't charge any service fees.
                    if (subTotal == 0)
                      serviceFees = 0;

                    return (
                      <Row key={2-index} className='mb-2'>
                        <Col xs={4} className="text-start cartColDetail">{item.NAME}</Col>
                        <Col xs={1} className="text-center cartColDetail">{item.cart_qty}</Col>
                        <Col xs={3} className="text-end cartColDetail">${itemPrice}</Col>
                          <Col xs={3} className="text-end cartColDetail">${lineTotal}</Col>
                          <Col xs={1} className='px-0' >
                                {item.ticket_type === 2 && item?.replacement === null && (
                                  loadingItemId === item.ticket_id ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                    </>
                                  ) : (
                                    <i
                                      className="bi bi-pencil-square"
                                      onClick={() => handleEditClick(item)}
                                    ></i>
                                  )
                                )}
                          </Col>
                      </Row>
                    );
                  })}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )
      }

        {/* Add Ons Section */}
        {
          <Row className="mb-2">
            <Col xs={12} className="mb-3">
              <Card>
                <Card.Body className='px-2'>
                  <Card.Title className="text-start">Enhance Your Experience</Card.Title>
                  <Row className='mt-2'>
                    <Col xs={2}></Col>
                    <Col xs={3} className="text-start fw-bold cartColHeading p-0">Item</Col>
                    <Col xs={2} className="text-center fw-bold cartColHeading">Qty</Col>
                    <Col xs={2} className="text-center fw-bold cartColHeading">Price</Col>
                    <Col xs={2} className="text-center fw-bold cartColHeading">Total</Col>
                    <Col xs={1}></Col>
                  </Row>
  
                  {
                    cartInfo
                    .filter((item) => item.ticket_type === 3)
                    .map((item,index) => {
                      item.rowId = '3-'+index;
                      let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
                      let lineTotal = itemPrice * item.cart_qty;
                      subTotal = subTotal + lineTotal;

                      itemPrice = itemPrice.toFixed(2);
                      lineTotal = lineTotal.toFixed(2);

                        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_TYPE;
                        
                        //straight dollar charge
                        if (serviceFeeType === 1) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * item.cart_qty;
                          
                        }
                        //percentage of ticket price
                        else if (serviceFeeType === 2) {
                          serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * lineTotal / 100;
        
                        }

                      //if the subtotal is $0 then don't charge any service fees.
                      if (subTotal == 0)
                        serviceFees = 0;

                      return (
                        <Row key={3-index} className='mb-2'>
                          <Col xs={2} className='px-0 m-0'><img className='cart-item-image' src={`${process.env.REACT_APP_CDN_URL}/${settings?.EVENT_URL}/${item.IMAGE_NAME}`} /></Col>
                          <Col xs={3} className="text-start cartColDetail cart-add-on-text p-0">{item.NAME}<br/><span className='cart-item-description'>{item.DESCRIPTION}</span></Col>
                          <Col xs={2} className="text-center cartColDetail p-0">{item.cart_qty}</Col>
                          <Col xs={2} className="text-center cartColDetail p-0">${itemPrice}</Col>
                            {item.cart_qty === 0 ? (
                              <Col xs={3} className='p-0 text-start' >
                                <Button style={{ marginLeft:'20px' }} 
                                  onClick={() => {
                                  handleEditClick(item);
                                }} className='btn-atc'>
                                  {loadingItemId === item.ticket_id ?  (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                      Adding...
                                    </>
                                  ) : (
                                    '  Add  '
                                  )}
                                </Button>
                              </Col>
                          ) : (
                            <>
                              <Col xs={2} className="text-center cartColDetail p-0">${lineTotal}</Col>
                              <Col xs={1} className='px-0' >
                                {item.ticket_type === 3 && item?.replacement === null && (
                                  loadingItemId === item.ticket_id ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                      ></span>
                                    </>
                                  ) : (
                                    <i
                                      className="bi bi-pencil-square"
                                      onClick={() => handleEditClick(item)}
                                    ></i>
                                  )
                                )}

                              </Col>
                            </>
                          )}
                        </Row>
                      );
                    })
                  }
                  <Row className='mt-2'>
                    <Col xs={12} className="text-center cart-col-detail" style={{ fontWeight: 'normal' }}>
                      <br/>
                      All merchandise items must be picked up at the time of entry to the show.
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        //)
        }
  
      {/* round up section*/}
      {(settings?.ES_CHARITY_ID && charityInfo) && (
        <Row className='mb-3'>
          <Col>
            <Card>
              <Card.Body className='px-2'>
                <Row>
                  <Col>
                    {charityInfo.ECH_LOGO && (
                      <p>
                        <img
                          style={{ width: 150 }}
                          src={`${process.env.REACT_APP_CDN_URL}/${settings?.EVENT_URL}/${charityInfo.ECH_LOGO}`}
                          alt={charityInfo.ECH_NAME}
                        />
                      </p>
                    )}
                    <p>
                      {charityInfo.ECH_NAME}<br/>
                      {charityInfo.ECH_DESCRIPTION}
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <RoundUpRow
                      totalCents={Math.round(totals?.grandTotal * 100)}
                      resetToken={roundupResetToken}
                      description={`Round up to support ${charityInfo.ECH_NAME} — every penny helps.`}
                      onToggle={(enabled, cents, reason) => {
                        setRoundupCents(enabled ? cents : 0);
                        setGrandTotalCents(
                          Math.round(totals?.grandTotal * 100) +
                          (enabled ? cents : 0)
                        );
                        if (reason === 'user' && enabled) {
                          saveCartRoundUp(cents/100);
                        }
                        if (reason === 'user' && !enabled) {
                          saveCartRoundUp(0);
                        }
                      }}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
      {/* Cart Summary */}
        <Row>
          <Col>
            <Card>
              <Card.Body className='px-2'>
              {discountTotal > 0 && (
                <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Promo Discount Applied:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    {/*(${discountTotal.toFixed(2)})8*/}
                    (${totals?.discountTotal.toFixed(2)})
                  </Col>
                </Row>
              )}
                <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Subtotal (incl. fees):
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    {/*${subTotal.toFixed(2)}*/}
                    ${(totals?.subTotal + totals?.serviceFees).toFixed(2)}
                  </Col>
                </Row>
                {/*<Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Service Fees:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    ${totals?.serviceFees.toFixed(2)}
                  </Col>
                </Row>*/}
                <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Tax:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    {/*${(subTotal * taxRate).toFixed(2)}*/}
                    ${totals?.taxTotal.toFixed(2)}
                  </Col>
                </Row>
                {(settings?.ES_CHARITY_ID) && (
                  <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Round Up:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    ${(roundupCents/100).toFixed(2)}
                  </Col>
                </Row>
                )}
                <Row>
                  <Col xs={9} className="text-end fw-bold cartColHeading">
                    Grand Total:
                  </Col>
                  <Col xs={3} className="text-end cartColDetail">
                    ${(grandTotalCents / 100).toFixed(2)}
                    {/*(subTotal + subTotal * taxRate + serviceFees).toFixed(2)*/}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>


        {/* Proceed to Checkout Button */}
        <Row>
          <Col>
              <br/><br/>
              <Button 
                disabled={ptcActive} 
                onClick={() => navigate('/checkout')}
                className='btn-atc'>
                Proceed to Checkout
              </Button>            
              <br/><br/>
          </Col>
        </Row>
      </Container>
    );
  }


function ticketHeader(inDate,inTime) {
    return (
        <>
        <Row>
        <Col className='text-start'>
            {inDate}, {inTime}
        </Col>
    </Row>
    <Row>
      <Col xs={4} className='text-start fw-bold cartColHeading pl-5'>Ticket</Col>
      <Col xs={1} className='text-center fw-bold cartColHeading px-0'>Qty</Col>
      <Col xs={3} className='text-end fw-bold cartColHeading'>Price</Col>
      <Col xs={3} className='text-end fw-bold cartColHeading'>Total</Col>
      <Col xs={1} >&nbsp;</Col>
    </Row>
    </>
    )
}

function CartPromoCode({cartInfo, refreshCart, onApplyingPromo, onClearRoundup}) {
  const { globalEventId } = useContext(EventContext);
  const { globalCartId } = useCart();

  const [localPromoCode, setLocalPromoCode] = useState(''); // Local state for the input field
  const { settings } = useContext(EventContext);

  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    if (settings) {
      setSettingsLoaded(true); // Mark settings as loaded
    }
  }, [settings]); 

  // Make sure the hook is called unconditionally
  const { promoCodeInput, promoCodeStatus, updatePromoCode,setPromoCodeClosed } = usePromoCodeInput(); // This should always be at the top level

  // Handle save when user clicks Enter
  //used by the 'apply promo' button at the top of the page
  async function handleSave() {

    onApplyingPromo?.(true);
    const saveResult = await updatePromoCode(globalEventId,localPromoCode); // Update global state with the local value
      
    if (saveResult) {
      try {
        const allowedKeys = ["cart_qty", 
                              "ticket_type", 
                              "ticket_id", 
                              "definition_id", 
                              "cart_item_id", 
                              "replacement", 
                              "toDelete",
                              "ticket_category"
                            ];

        // Map over newCartContents and extract only allowed keys
        const filteredNewCartContents = cartInfo.map(item =>
          Object.fromEntries(Object.entries(item).filter(([key]) => allowedKeys.includes(key)))
        );

        await syncCartToServer({
            eventId:globalEventId,
            cartID: globalCartId.cartID,
            promoCode: localPromoCode,
            cartContents: filteredNewCartContents
        });

        onClearRoundup?.();
        refreshCart();
        setPromoCodeClosed(1);  //prevent the home page pop-up from being displayed
      } catch (error) {
        console.error('Error syncing cart to server:', error);
      } finally {
        onApplyingPromo?.(false);
      }
    }
  }

  if (!settingsLoaded || settings?.ES_PROMO_POPUP === 0) return null; // Render nothing if the modal is closed or promocode is already set.

  return (
    <Row className='mb-4'>
      <Col>
      <Card>
        <Card.Body>
          <h5>Please enter your promo code</h5>
          <input
            type="text"
            placeholder={promoCodeInput ? `${promoCodeInput}` : 'Type here...'}
            value={localPromoCode} // Bind to local state
            onChange={(e) => setLocalPromoCode(e.target.value)} // Update local state on input change
          />
          <button onClick={handleSave}>Apply</button><br></br>
          <p>{promoCodeStatus}</p>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

function RoundUpRow({ totalCents, onToggle, resetToken, description = "Round up to support our charity" }) {
  const [enabled, setEnabled] = React.useState(false);

  // work in cents only
  const roundupCents = React.useMemo(() => {
    const r = totalCents % 100;
    return r === 0 ? 0 : 100 - r;
  }, [totalCents]);

  // If totals change and it's enabled, let parent recompute totals (no save)
  React.useEffect(() => {
    if (enabled) onToggle(true, roundupCents, 'recalc');
  }, [enabled, roundupCents, onToggle]);

  React.useEffect(() => {
    if (enabled) {
      setEnabled(false);
      onToggle(false, roundupCents, 'reset');
    }
  }, [resetToken]); // only runs when parent bumps the token

  const handleChange = (e) => {
    const next = e.target.checked;
    setEnabled(next);
    onToggle(next, roundupCents, 'user'); // only this should trigger a save
  };

  return (
    <label>
      <input type="checkbox" checked={enabled} onChange={handleChange} />&nbsp;
      Round Up By ${(roundupCents / 100).toFixed(2)}
    </label>
  );
}

function calcTotals(cartInfo, cartFees, taxRate) {
  let subTotal = 0;
  let grandTotal = 0;
  let discountTotal = 0;
  let ticketQty = 0;
  let serviceFees = 0;
  let taxTotal = 0

    //ticket totals and service fees
    cartInfo
    .filter((item) => item.ticket_type === 1 || item.ticket_type === 4 || item.ticket_type === 5)
    .map((item,index) => {
        
        let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
        let lineTotal = itemPrice * item.cart_qty;
        
        //don't charge service fees on replacement tickets
        if ((item.ticket_type === 1) && item?.replacement === null) {
          ticketQty += item.cart_qty;
          const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_TYPE;
          if (serviceFeeType === 1) {
              if (itemPrice > 0)
                  serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_AMOUNT * item.cart_qty;
          } else if (serviceFeeType === 2) {
              if (itemPrice > 0)
                  serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 1)?.EF_FEE_AMOUNT * lineTotal / 100;
          }
      } else if ((item.ticket_type === 5) && item?.replacement === null) {
          ticketQty += item.cart_qty;
          const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_TYPE;
          if (serviceFeeType === 1) {
              if (itemPrice > 0)
                  serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * item.cart_qty;
          } else if (serviceFeeType === 2) {
              if (itemPrice > 0)
                  serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 5)?.EF_FEE_AMOUNT * lineTotal / 100;
          }
      }

        subTotal = subTotal + lineTotal;

        if (item?.promo_price > 0)
        discountTotal = discountTotal + ((item.base_price - item.promo_price) * item.cart_qty);

        itemPrice = itemPrice.toFixed(2);
        lineTotal = lineTotal.toFixed(2);


        //if the subtotal for regular tickets is $0 then don't charge any service fees on them.
        if (subTotal == 0)
        serviceFees = 0;

    })

    //passes totals and service fees
    cartInfo
    .filter((item) => item.ticket_type === 2)
    .map((item,index) => {
    
        item.rowId = '2-'+index;
        let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
        let lineTotal = itemPrice * item.cart_qty;
        subTotal = subTotal + lineTotal;

        if (item?.promo_price > 0)
        discountTotal = discountTotal + ((item.base_price - item.promo_price) * item.cart_qty);

            if (item.ticket_type === 2 && item?.replacement === null) {
            ticketQty += item.cart_qty
            const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_TYPE;
            
            //straight dollar charge
            if (serviceFeeType === 1) {
                serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * item.cart_qty;
                
            }
            //percentage of ticket price
            else if (serviceFeeType === 2) {
                serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 2)?.EF_FEE_AMOUNT * lineTotal / 100;

            }
            }


        itemPrice = itemPrice.toFixed(2);
        lineTotal = lineTotal.toFixed(2);

        //if the subtotal is $0 then don't charge any service fees.
        if (subTotal == 0)
            serviceFees = 0;

    })

    //merchandise totals and service fees
    cartInfo
    .filter((item) => item.ticket_type === 3)
    .map((item,index) => {
        item.rowId = '3-'+index;
        let itemPrice = item.promo_price === null ? item.base_price : item.promo_price;
        let lineTotal = itemPrice * item.cart_qty;
        subTotal = subTotal + lineTotal;

        itemPrice = itemPrice.toFixed(2);
        lineTotal = lineTotal.toFixed(2);

        const serviceFeeType = cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_TYPE;
        
        //straight dollar charge
        if (serviceFeeType === 1) {
            serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * item.cart_qty;
            
        }
        //percentage of ticket price
        else if (serviceFeeType === 2) {
            serviceFees += cartFees.find(fee => fee.EF_TICKET_TYPE === 3)?.EF_FEE_AMOUNT * lineTotal / 100;

        }

        //if the subtotal is $0 then don't charge any service fees.
        if (subTotal == 0)
        serviceFees = 0;
    })

    taxTotal = (subTotal + serviceFees) * taxRate.toFixed(2);
    grandTotal = (subTotal + taxTotal + serviceFees).toFixed(2);

    return {
    subTotal,
    discountTotal,
    ticketQty,
    serviceFees,
    taxTotal,
    grandTotal,
  };
    
}
